import type { EChartsTheme } from '../types';

// Multi-series grid layout.
const ITEMS_PER_COLUMN = 8;
const MAX_COLUMNS = 3;
const MULTI_SERIES_COLUMN_GAP = 16;
const MULTI_SERIES_LABEL_MIN_WIDTH = 80;

// Click-to-drill-down hint icon shared by sankey + spendingTrend tooltips.
export const TOOLTIP_CLICK_HINT_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" aria-hidden="true"><g stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.444"><path d="m16.59 17.346 1.527 3.017a.554.554 0 0 1-.244.744l-1.646.833a.554.554 0 0 1-.744-.244l-1.527-3.017-2.632 1.647.006-9.135c0-.456.521-.716.886-.442l7.258 5.452z"/><path d="M5 11H3M21 11h-2M7.05 15.95l-1.41 1.41M18.36 4.64l-1.41 1.41M12 2v2M5.64 4.64l1.41 1.41"/></g></svg>';

export const buildTooltipConfig = (theme: EChartsTheme) => ({
  trigger: 'axis' as const,
  axisPointer: { type: 'shadow' as const, shadowStyle: { opacity: 0 } },
  confine: true,
  backgroundColor: theme.bgColor,
  borderColor: theme.borderColor,
  borderWidth: 1,
  borderRadius: theme.tooltipBorderRadius,
  // matches amcharts4: shape.padding[2] = 8px
  padding: 8,
  extraCssText: 'box-shadow: none;',
  textStyle: { color: theme.textSecondaryColor, fontSize: 12 },
});

// Figma drop shadow for a tooltip that floats as its own card (borderless, shadowed) rather than
// sitting in the bordered `buildTooltipConfig` box. Shared by the line/area charts and
// `creditHistory`, which differ in how the tooltip is triggered but not in how the card looks.
export const FLOATING_TOOLTIP_SHADOW = 'box-shadow: 0px 0px 2px rgba(51,51,51,0.48), 0px 4px 10px rgba(51,51,51,0.16);';

// Figma-spec overrides for line/area charts. Spread over buildTooltipConfig(theme).
export const buildLineTooltipShell = (theme: EChartsTheme) => ({
  trigger: 'axis' as const,
  // SIMPL-34452: solid hover line (ECharts axisPointer lineStyle.type defaults to 'dashed')
  // in the chart.Today color.
  axisPointer: { type: 'line' as const, lineStyle: { color: theme.todayColor, width: 1, type: 'solid' as const } },
  borderWidth: 0,
  borderRadius: 6,
  padding: 0,
  extraCssText: FLOATING_TOOLTIP_SHADOW,
});

// --- Primitives ---

/** Escape untrusted text before interpolating it into ECharts tooltip HTML. */
export const escapeTooltipHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const buildTooltipHeader = (category: string, theme: EChartsTheme): string =>
  `<div style="color:${theme.textSecondaryColor};font-size:12px;margin-bottom:4px;">${escapeTooltipHtml(category)}</div>`;

export const buildColorDot = (color: string, opacity?: number): string =>
  `<div style="width:12px;height:12px;border-radius:50%;background:${color};${
    opacity === undefined ? '' : `opacity:${opacity};`
  }flex-shrink:0;"></div>`;

const buildColorLine = (color: string): string =>
  `<div style="width:16px;height:2px;background:${color};flex-shrink:0;"></div>`;

export const buildTooltipDivider = (theme: EChartsTheme): string =>
  `<div style="height:1px;background:${theme.dividerColor};margin:6px 0;"></div>`;

export const buildTooltipWrapper = (content: string): string => `<div style="padding:8px;">${content}</div>`;

// --- Layout groups ---
// Each returns a content string (no outer padding wrapper).
// Callers prepend buildTooltipHeader and wrap with buildTooltipWrapper.

// Single series: "Name $value" on one line, no circle. Used when orderedFields.length === 1.
export const buildSingleSeriesBody = (name: string, formattedValue: string, theme: EChartsTheme): string =>
  `<div style="color:${theme.textColor};font-size:14px;">${name} <span style="font-weight:500;">${formattedValue}</span></div>`;

// Summary/total row: label + right-aligned amount, rendered after a divider at the bottom of
// the breakdown tooltip (e.g. "Net Worth" / "Total"). Uses the same 3-column grid as the account
// rows so the marker/label/amount line up beneath them. Pass `markerColor` to show a line segment
// for a real total series (e.g. Net Worth); omit it for a markerless computed total (e.g. Savings).
export const buildTooltipTotalRow = (
  label: string,
  formattedValue: string,
  theme: EChartsTheme,
  markerColor?: string,
): string => {
  const marker = markerColor ? buildColorLine(markerColor) : '<span></span>';
  return (
    `<div style="display:grid;grid-template-columns:16px minmax(${MULTI_SERIES_LABEL_MIN_WIDTH}px,1fr) auto;align-items:center;column-gap:8px;width:100%;">${marker}` +
    `<span style="color:${theme.textColor};font-size:14px;">${label}</span>` +
    `<span style="text-align:right;color:${theme.textColor};font-size:14px;font-weight:500;white-space:nowrap;">${formattedValue}</span></div>`
  );
};

// Single highlight: circle + name + value in a flex row. Used when one series is hovered.
export const buildSingleHighlightBody = (
  color: string,
  name: string,
  formattedValue: string,
  theme: EChartsTheme,
): string =>
  `<div style="display:flex;align-items:center;gap:6px;">${buildColorDot(color)}<span style="color:${
    theme.textColor
  };font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px;">${name}</span>` +
  `<span style="color:${theme.textColor};font-size:14px;font-weight:500;white-space:nowrap;">${formattedValue}</span>` +
  `</div>`;

// Stacked swatch: circle + name and value stacked beside it. Shared by the pie/donut tooltips and
// the Spending Plan donut so they read identically. An empty `name` renders the amount alone
// (mini-card variant). Relies on the buildTooltipConfig shell for background, border, and padding —
// do not wrap it with buildTooltipWrapper or the padding doubles.
export const buildStackedSwatchBody = (
  color: string,
  name: string,
  formattedValue: string,
  theme: EChartsTheme,
): string => {
  const nameLine = name ? `<span style="color:${theme.textSecondaryColor};font-size:14px;">${name}</span>` : '';
  return (
    `<div style="display:grid;grid-template-columns:12px 1fr;grid-column-gap:10px;align-items:start;">` +
    `<div style="margin-top:6px;">${buildColorDot(color)}</div>` +
    `<div style="display:flex;flex-direction:column;">${nameLine}` +
    `<span style="color:${theme.textSecondaryColor};font-size:14px;">${formattedValue}</span>` +
    `</div></div>`
  );
};

// --- Multi-series grid ---

// Multi-series grid: each item is a row (circle | name | right-aligned amount). Rows spill into
// up to MAX_COLUMNS side-by-side columns once they exceed ITEMS_PER_COLUMN, so long account lists
// stay compact instead of growing one tall column. Amounts right-align within each column.
export const buildMultiSeriesGridBody = (
  items: Array<{ color: string; name: string; formattedValue: string }>,
  theme: EChartsTheme,
  markerOpacity?: number,
  stretchColumns = false,
  summaryRow = '',
): string => {
  const numColumns = Math.max(1, Math.min(MAX_COLUMNS, Math.ceil(items.length / ITEMS_PER_COLUMN)));
  const itemsPerColumn = Math.ceil(items.length / numColumns);
  const labelColumn = stretchColumns ? `minmax(${MULTI_SERIES_LABEL_MIN_WIDTH}px,1fr)` : 'minmax(0,1fr)';

  let html = `<div style="display:flex;gap:${MULTI_SERIES_COLUMN_GAP}px;margin-top:4px;${stretchColumns ? 'width:100%;' : ''}">`;
  for (let col = 0; col < numColumns; col++) {
    const slice = items.slice(col * itemsPerColumn, (col + 1) * itemsPerColumn);
    if (slice.length === 0) continue;
    html += `<div style="display:grid;grid-template-columns:16px ${labelColumn} auto;align-items:center;row-gap:4px;column-gap:8px;${
      stretchColumns ? 'flex:1;min-width:0;' : ''
    }">`;
    for (const item of slice) {
      html +=
        `${buildColorDot(
          item.color,
          markerOpacity,
        )}<span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px;color:${
          theme.textColor
        };font-size:14px;">${item.name}</span>` +
        `<span style="white-space:nowrap;text-align:right;color:${theme.textColor};font-size:14px;font-weight:500;">${item.formattedValue}</span>`;
    }
    html += '</div>';
  }
  html += '</div>';

  if (summaryRow) {
    const gapWidth = (numColumns - 1) * MULTI_SERIES_COLUMN_GAP;
    const summaryWidth = !stretchColumns || numColumns === 1 ? '100%' : `calc((100% - ${gapWidth}px) / ${numColumns})`;
    html += `${buildTooltipDivider(theme)}<div style="width:${summaryWidth};">${summaryRow}</div>`;
  }

  return html;
};
